import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import TCClient from '../../../../../../client';

type ToastState = {
  isOpen: boolean;
  message: string;
  type: 'SUCCESS' | 'FAILURE';
};

type OidcFormData = {
  issuerUrl?: string;
  clientId?: string;
  clientSecret?: string;
  authorizationEndpoint?: string;
  tokenEndpoint?: string;
  userInfoEndpoint?: string;
  jwksEndpoint?: string;
  isManualConfig?: boolean;
  emailDomains?: string;
};

export const useOidcTestConnection = (
  organizationToken: string | undefined,
  setToast: React.Dispatch<React.SetStateAction<ToastState>>,
  formData?: OidcFormData
) => {
  const [isInitiatingTest, setIsInitiatingTest] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const popupIntervalRef = useRef<NodeJS.Timeout | number | null>(null);
  const popupWindowRef = useRef<Window | null>(null);
  const messageListenerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const testTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (popupIntervalRef.current) clearInterval(popupIntervalRef.current);
      if (testTimeoutRef.current) clearTimeout(testTimeoutRef.current);
      if (messageListenerRef.current)
        window.removeEventListener('message', messageListenerRef.current);
      try {
        if (popupWindowRef.current && !popupWindowRef.current.closed) {
          popupWindowRef.current.close();
        }
      } catch {
        // COOP blocks cross-origin window.closed access
      }
      popupWindowRef.current = null;
    };
  }, []);

  const testOidcConnection = useCallback(async () => {
    if (!organizationToken) return;

    try {
      if (popupWindowRef.current && !popupWindowRef.current.closed) {
        popupWindowRef.current.close();
      }
    } catch {
      // COOP blocks cross-origin window.closed access — treat as closed
    }
    popupWindowRef.current = null;
    setIsPopupOpen(false);
    if (popupIntervalRef.current) {
      clearInterval(popupIntervalRef.current);
      popupIntervalRef.current = null;
    }
    if (testTimeoutRef.current) {
      clearTimeout(testTimeoutRef.current);
      testTimeoutRef.current = null;
    }
    if (messageListenerRef.current) {
      window.removeEventListener('message', messageListenerRef.current);
      messageListenerRef.current = null;
    }

    setIsInitiatingTest(true);

    // Open blank popup synchronously to preserve transient user activation before the async request
    const placeholderPopup = window.open(
      'about:blank',
      `popup-${crypto.randomUUID()}`,
      'width=600,height=700,location=yes,toolbar=no'
    );
    popupWindowRef.current = placeholderPopup; // store immediately so unmount cleanup can close it

    let challengeUrl: string;
    try {
      const res = await TCClient.adminOrganizations.initiateOidcTestConnection({
        organizationId: organizationToken,
        windowOrigin: window.location.origin,
        issuerUrl: formData?.issuerUrl,
        clientId: formData?.clientId,
        clientSecret: formData?.clientSecret,
        authorizationEndpoint: formData?.authorizationEndpoint,
        tokenEndpoint: formData?.tokenEndpoint,
        userInfoEndpoint: formData?.userInfoEndpoint,
        jwksEndpoint: formData?.jwksEndpoint,
        isManualConfig: formData?.isManualConfig,
        emailDomains: formData?.emailDomains,
      });
      challengeUrl = res.challengeUrl;
    } catch (error) {
      setIsInitiatingTest(false);
      try {
        placeholderPopup?.close();
      } catch {
        // ignore
      }
      popupWindowRef.current = null;
      if (isMountedRef.current) {
        setToast({
          isOpen: true,
          message: 'Failed to initiate connection test. Please try again.',
          type: 'FAILURE',
        });
      }
      Sentry.logger.error('Error initiating OIDC test connection', { error });
      return;
    }

    // Check mount status after async operation before touching any state or the popup
    if (!isMountedRef.current) {
      try {
        placeholderPopup?.close();
      } catch {
        // ignore
      }
      popupWindowRef.current = null;
      return;
    }

    setIsInitiatingTest(false);

    if (!placeholderPopup) {
      setToast({
        isOpen: true,
        message: 'Unable to open popup window. Please check your browser settings.',
        type: 'FAILURE',
      });
      return;
    }

    let idpOrigin: string;
    try {
      const challengeUrlParsed = new URL(challengeUrl);
      idpOrigin = challengeUrlParsed.origin;
    } catch {
      try {
        placeholderPopup.close();
      } catch {
        // ignore
      }
      popupWindowRef.current = null;
      setToast({
        isOpen: true,
        message: 'Failed to initiate connection test. Please try again.',
        type: 'FAILURE',
      });
      return;
    }

    // Navigate existing popup to the challenge URL (avoids opening a second popup)
    try {
      placeholderPopup.location.href = challengeUrl;
    } catch {
      try {
        placeholderPopup.close();
      } catch {
        // ignore
      }
      popupWindowRef.current = null;
      setToast({
        isOpen: true,
        message: 'Unable to open popup window. Please check your browser settings.',
        type: 'FAILURE',
      });
      return;
    }

    setIsPopupOpen(true);

    const cleanup = () => {
      if (testTimeoutRef.current) {
        clearTimeout(testTimeoutRef.current);
        testTimeoutRef.current = null;
      }
      if (messageListenerRef.current) {
        window.removeEventListener('message', messageListenerRef.current);
        messageListenerRef.current = null;
      }
      if (popupIntervalRef.current) {
        clearInterval(popupIntervalRef.current);
        popupIntervalRef.current = null;
      }
      setIsPopupOpen(false);
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== idpOrigin) return;
      if (event.source !== popupWindowRef.current) return;
      const { type, success, message } = (event.data ?? {}) as {
        type?: string;
        success?: boolean;
        message?: string;
      };
      if (type !== 'oidcTestResult') return;

      cleanup();
      popupWindowRef.current = null;
      if (isMountedRef.current) {
        setToast({
          isOpen: true,
          message: message ?? (success ? 'OIDC login successful!' : 'OIDC connection test failed.'),
          type: success ? 'SUCCESS' : 'FAILURE',
        });
      }
    };

    messageListenerRef.current = handleMessage;
    window.addEventListener('message', handleMessage);

    testTimeoutRef.current = setTimeout(() => {
      cleanup();
      try {
        if (popupWindowRef.current && !popupWindowRef.current.closed) {
          popupWindowRef.current.close();
        }
      } catch {
        // COOP blocks cross-origin window.closed access — treat as closed
      }
      popupWindowRef.current = null;
      if (isMountedRef.current) {
        setToast({ isOpen: true, message: 'Connection test timed out.', type: 'FAILURE' });
      }
    }, 5 * 60 * 1000);

    // Track consecutive COOP errors; after 5 stop polling but keep message listener + timeout active
    let consecutiveCoopErrors = 0;
    popupIntervalRef.current = setInterval(() => {
      try {
        if (popupWindowRef.current?.closed) {
          cleanup();
          popupWindowRef.current = null;
        } else {
          consecutiveCoopErrors = 0;
        }
      } catch {
        consecutiveCoopErrors++;
        if (consecutiveCoopErrors >= 5) {
          if (popupIntervalRef.current) {
            clearInterval(popupIntervalRef.current);
            popupIntervalRef.current = null;
          }
        }
      }
    }, 500);
  }, [organizationToken, setToast, formData]);

  return { testOidcConnection, isInitiatingTest, isPopupOpen, popupWindowRef };
};
